/**
 * OTIF Grade Thresholds Section - CR-6267
 *
 * Fully configurable colour grading for OTIF, presented with the same
 * card-list / click-to-edit interaction as the Supplier Grade Thresholds
 * tab. Grade id, label, colour and description mirror that tab; the
 * percentage band is OTIF's own.
 */
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Loader2, RotateCcw, Pencil, X, Check, Plus, Trash2, TrendingUp } from 'lucide-react';
import { OTIFGradeThreshold, CreateOTIFGradeThresholdData } from '../../../lib/api';
import { OTIFGradeEditData } from './useOTIFGradeThresholds';

interface OTIFGradeThresholdsSectionProps {
  bands: OTIFGradeThreshold[];
  editingBand: number | null;
  bandEditData: OTIFGradeEditData | null;
  savingBand: boolean;
  showAddBand: boolean;
  newBandData: CreateOTIFGradeThresholdData;
  addingBand: boolean;
  resettingBands: boolean;
  onSetShowAddBand: (show: boolean) => void;
  onResetBands: () => void;
  onEditBand: (band: OTIFGradeThreshold) => void;
  onCancelEditBand: () => void;
  onSaveBand: (bandId: number) => void;
  onBandFieldChange: (field: string, value: string | number) => void;
  onAddBand: () => void;
  onDeleteBand: (bandId: number, label: string) => void;
  onNewBandFieldChange: (field: string, value: string | number) => void;
}

export function OTIFGradeThresholdsSection({
  bands,
  editingBand,
  bandEditData,
  savingBand,
  showAddBand,
  newBandData,
  addingBand,
  resettingBands,
  onSetShowAddBand,
  onResetBands,
  onEditBand,
  onCancelEditBand,
  onSaveBand,
  onBandFieldChange,
  onAddBand,
  onDeleteBand,
  onNewBandFieldChange,
}: OTIFGradeThresholdsSectionProps) {
  const sorted = [...bands].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-4 border-t pt-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-medium flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            OTIF Grade Thresholds
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Define the OTIF percentage bands, labels and colours used for OTIF colour coding on
            dashboards. Click a grade to edit it. These are independent of Supplier Grade Thresholds.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => onSetShowAddBand(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Grade
          </Button>
          <Button variant="outline" size="sm" onClick={onResetBands} disabled={resettingBands}>
            {resettingBands ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-2" />
            )}
            Reset to Defaults
          </Button>
        </div>
      </div>

      {sorted.length > 0 ? (
        <div className="space-y-4">
          {sorted.map((band) => (
            <div key={band.id}>
              {editingBand === band.id && bandEditData ? (
                <OTIFGradeEditForm
                  band={band}
                  bandEditData={bandEditData}
                  savingBand={savingBand}
                  onCancelEditBand={onCancelEditBand}
                  onSaveBand={onSaveBand}
                  onBandFieldChange={onBandFieldChange}
                  onDeleteBand={onDeleteBand}
                />
              ) : (
                <OTIFGradeViewItem band={band} onEditBand={onEditBand} />
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground text-center py-4">
          No OTIF grades configured. Click "Reset to Defaults" to create the default grades.
        </p>
      )}

      {showAddBand && (
        <AddOTIFGradeForm
          newBandData={newBandData}
          addingBand={addingBand}
          onSetShowAddBand={onSetShowAddBand}
          onAddBand={onAddBand}
          onNewBandFieldChange={onNewBandFieldChange}
        />
      )}
    </div>
  );
}

interface OTIFGradeEditFormProps {
  band: OTIFGradeThreshold;
  bandEditData: OTIFGradeEditData;
  savingBand: boolean;
  onCancelEditBand: () => void;
  onSaveBand: (bandId: number) => void;
  onBandFieldChange: (field: string, value: string | number) => void;
  onDeleteBand: (bandId: number, label: string) => void;
}

function OTIFGradeEditForm({
  band,
  bandEditData,
  savingBand,
  onCancelEditBand,
  onSaveBand,
  onBandFieldChange,
  onDeleteBand,
}: OTIFGradeEditFormProps) {
  return (
    <div
      className="p-4 border rounded-lg space-y-4"
      style={{ borderLeftColor: bandEditData.color, borderLeftWidth: '4px' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
            style={{ backgroundColor: bandEditData.color }}
          >
            {bandEditData.grade || '?'}
          </div>
          <div className="font-medium text-lg">
            Edit OTIF Grade {bandEditData.label || bandEditData.grade}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancelEditBand} disabled={savingBand}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button size="sm" onClick={() => onSaveBand(band.id)} disabled={savingBand}>
            {savingBand ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-2">
          <Label htmlFor={`otif-grade-${band.id}`}>Grade ID</Label>
          <Input
            id={`otif-grade-${band.id}`}
            value={bandEditData.grade}
            onChange={(e) => onBandFieldChange('grade', e.target.value)}
            placeholder="e.g., A, B, C"
            maxLength={10}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`otif-label-${band.id}`}>Display Label</Label>
          <Input
            id={`otif-label-${band.id}`}
            value={bandEditData.label}
            onChange={(e) => onBandFieldChange('label', e.target.value)}
            placeholder="e.g., Platinum, Gold"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`otif-min-${band.id}`}>Min OTIF %</Label>
          <Input
            id={`otif-min-${band.id}`}
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={bandEditData.min_otif_percent}
            onChange={(e) => onBandFieldChange('min_otif_percent', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`otif-max-${band.id}`}>Max OTIF %</Label>
          <Input
            id={`otif-max-${band.id}`}
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={bandEditData.max_otif_percent}
            onChange={(e) => onBandFieldChange('max_otif_percent', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`otif-color-${band.id}`}>Color</Label>
          <div className="flex items-center gap-2">
            <input
              id={`otif-color-${band.id}`}
              type="color"
              value={bandEditData.color}
              onChange={(e) => onBandFieldChange('color', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border"
            />
            <Input
              value={bandEditData.color}
              onChange={(e) => onBandFieldChange('color', e.target.value)}
              placeholder="#22C55E"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1 space-y-2">
          <Label htmlFor={`otif-description-${band.id}`}>Description</Label>
          <Input
            id={`otif-description-${band.id}`}
            value={bandEditData.description}
            onChange={(e) => onBandFieldChange('description', e.target.value)}
            placeholder="Description for this OTIF grade"
          />
        </div>
        <div className="pt-6">
          <Button
            variant="destructive"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteBand(band.id, band.display_label || band.label || band.grade);
            }}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

interface OTIFGradeViewItemProps {
  band: OTIFGradeThreshold;
  onEditBand: (band: OTIFGradeThreshold) => void;
}

function OTIFGradeViewItem({ band, onEditBand }: OTIFGradeViewItemProps) {
  const label = band.display_label || band.label || `Grade ${band.grade}`;

  return (
    <div
      className="flex items-center gap-4 p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
      style={{ borderLeftColor: band.color, borderLeftWidth: '4px' }}
      onClick={() => onEditBand(band)}
    >
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
        style={{ backgroundColor: band.color }}
      >
        {band.grade}
      </div>
      <div className="flex-1">
        <div className="font-medium flex items-center gap-2">
          {label}
          {band.label && band.label !== band.grade && (
            <Badge variant="secondary" className="text-xs">
              Grade {band.grade}
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground">{band.description}</div>
      </div>
      <div className="text-right">
        <div className="font-medium">
          {band.min_otif_percent} - {band.max_otif_percent}%
        </div>
        <div className="text-sm text-muted-foreground">OTIF Range</div>
      </div>
      <Pencil className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

interface AddOTIFGradeFormProps {
  newBandData: CreateOTIFGradeThresholdData;
  addingBand: boolean;
  onSetShowAddBand: (show: boolean) => void;
  onAddBand: () => void;
  onNewBandFieldChange: (field: string, value: string | number) => void;
}

function AddOTIFGradeForm({
  newBandData,
  addingBand,
  onSetShowAddBand,
  onAddBand,
  onNewBandFieldChange,
}: AddOTIFGradeFormProps) {
  return (
    <div className="mt-4 p-4 border rounded-lg space-y-4 bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
            style={{ backgroundColor: newBandData.color }}
          >
            {newBandData.grade || '?'}
          </div>
          <div className="font-medium text-lg">Add New OTIF Grade</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSetShowAddBand(false)}
            disabled={addingBand}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button size="sm" onClick={onAddBand} disabled={addingBand || !newBandData.grade.trim()}>
            {addingBand ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            Add Grade
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-2">
          <Label htmlFor="new-otif-grade">Grade ID *</Label>
          <Input
            id="new-otif-grade"
            value={newBandData.grade}
            onChange={(e) => onNewBandFieldChange('grade', e.target.value)}
            placeholder="e.g., A+, B+, S"
            maxLength={10}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-otif-label">Display Label</Label>
          <Input
            id="new-otif-label"
            value={newBandData.label}
            onChange={(e) => onNewBandFieldChange('label', e.target.value)}
            placeholder="e.g., Platinum, Elite"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-otif-min">Min OTIF %</Label>
          <Input
            id="new-otif-min"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={newBandData.min_otif_percent}
            onChange={(e) => onNewBandFieldChange('min_otif_percent', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-otif-max">Max OTIF %</Label>
          <Input
            id="new-otif-max"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={newBandData.max_otif_percent}
            onChange={(e) => onNewBandFieldChange('max_otif_percent', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-otif-color">Color</Label>
          <div className="flex items-center gap-2">
            <input
              id="new-otif-color"
              type="color"
              value={newBandData.color}
              onChange={(e) => onNewBandFieldChange('color', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border"
            />
            <Input
              value={newBandData.color}
              onChange={(e) => onNewBandFieldChange('color', e.target.value)}
              placeholder="#3B82F6"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="new-otif-description">Description</Label>
        <Input
          id="new-otif-description"
          value={newBandData.description}
          onChange={(e) => onNewBandFieldChange('description', e.target.value)}
          placeholder="Description for this OTIF grade"
        />
      </div>
    </div>
  );
}
